"use client";

import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Carousel } from "@farm-junction/design-library";
import ImageWrapper from "@/component/ui/ImageWrapper";
import {
    getImagePageCdnUrl,
    getImagePageLcpSrcSet,
    getImagePageLcpUrl,
    HERO_LCP_ASPECT,
    HERO_LCP_SIZES,
} from "./galleryData";

export type ImagePageCarouselSlide = {
    id: string | number;
    src: string;
    alt: string;
    title: string;
    label: string;
    swatch?: string;
};

type ImagePageCarouselProps = {
    slides: ImagePageCarouselSlide[];
    startIndex?: number;
    activeIndex?: number;
    jumpKey?: number;
    alignKey?: number;
    onSlideClick?: (index: number) => void;
    onIndexChange?: (index: number) => void;
    onNavigate?: (index: number) => void;
    onStep?: (delta: number) => boolean | void;
    countOffset?: number;
    countTotal?: number;
    frameClassName?: string;
    sizes?: string;
    priority?: boolean;
    isMobile?: boolean;
    variant?: "hero" | "popup";
};

const CHIP_CLASS =
    "absolute z-10 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white md:bottom-4 md:px-3 md:text-xs";

const MAIN_CAROUSEL_CLASS = [
    "w-full overflow-visible",
    "[&>div>div]:!pb-0",
    "[&>button]:!h-10 [&>button]:!w-10 [&>button]:!rounded-full",
    "[&>button]:!border [&>button]:!border-[#E5E5E5] [&>button]:!bg-white [&>button]:!shadow-md",
    "[&>button]:!translate-x-0 [&>button]:!-translate-y-1/2",
    "[&>button]:!opacity-100 [&>button]:disabled:!opacity-100",
    "max-md:[&>button[aria-label='Previous']]:!hidden",
    "max-md:[&>button[aria-label='Next']]:!hidden",
    "[&>button[aria-label='Previous']]:!left-3 md:[&>button[aria-label='Previous']]:!left-4",
    "[&>button[aria-label='Next']]:!right-3 md:[&>button[aria-label='Next']]:!right-4",
].join(" ");

const POPUP_CAROUSEL_SHARED = [
    "[&>div>div]:!pb-0",
    "[&>button[aria-label='Previous']]:!hidden",
    "[&>button[aria-label='Next']]:!hidden",
].join(" ");

const POPUP_IMAGE_CAROUSEL_CLASS = ["w-full md:w-[901px]", POPUP_CAROUSEL_SHARED].join(" ");

const PopupChevron = ({ direction, color }: { direction: "prev" | "next"; color: string }) => (
    <svg width="12" height="20" viewBox="0 0 7 13" fill="none" aria-hidden>
        <path
            d={direction === "prev" ? "M6 1L1 6.5L6 12" : "M1 1L6 6.5L1 12"}
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const THUMB_CAROUSEL_CLASS = [
    "w-full",
    "[&>div]:!overflow-x-auto",
    "[&>div]:!overflow-y-hidden",
    "[&>div]:!snap-none",
    "[&>div]:touch-pan-x",
    "[&>div]:[-webkit-overflow-scrolling:touch]",
    "[&>div]:[scrollbar-width:none]",
    "[&>div]:[-ms-overflow-style:none]",
    "[&>div]:[&::-webkit-scrollbar]:hidden",
    "[&>div>div]:!pb-0",
    "[&>div>div>*]:!snap-none",
].join(" ");

const TAP_THRESHOLD = 12;
const MOBILE_MAX_WIDTH = 767;
const SWIPE_THRESHOLD_PX = 48;

const wrapIndex = (index: number, total: number) => {
    if (total <= 0) return 0;
    return ((index % total) + total) % total;
};

const rotate = <T,>(items: T[], start: number) => {
    if (!items.length) return items;
    const offset = wrapIndex(start, items.length);
    if (!offset) return items;
    return [...items.slice(offset), ...items.slice(0, offset)];
};

const isMobileViewport = () =>
    typeof window !== "undefined" && window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

/** Mobile swipes need a previous slide in the DOM; hero only kept current+next. */
const buildSlideWindow = (
    slides: ImagePageCarouselSlide[],
    startIndex: number,
    total: number,
    isMobile: boolean,
    isPopup: boolean,
): { rendered: ImagePageCarouselSlide[]; centerOffset: number; threeUp: boolean } => {
    if (total <= 0) {
        return { rendered: [], centerOffset: 0, threeUp: false };
    }
    if (total === 1) {
        return { rendered: slides.slice(0, 1), centerOffset: 0, threeUp: false };
    }

    const threeUp = isMobile && total > 2;
    if (threeUp) {
        const prev = wrapIndex(startIndex - 1, total);
        const next = wrapIndex(startIndex + 1, total);
        return {
            rendered: [slides[prev], slides[startIndex], slides[next]],
            centerOffset: 1,
            threeUp: true,
        };
    }

    const orderedSlides = rotate(slides, startIndex);
    if (isPopup) {
        return { rendered: orderedSlides, centerOffset: 0, threeUp: false };
    }
    return {
        rendered: orderedSlides.slice(0, Math.min(2, total)),
        centerOffset: 0,
        threeUp: false,
    };
};

const indexForWindowSlot = (
    slotIndex: number,
    startIndex: number,
    total: number,
    threeUp: boolean,
) => {
    if (!threeUp) return wrapIndex(startIndex + slotIndex, total);
    if (slotIndex === 0) return wrapIndex(startIndex - 1, total);
    if (slotIndex === 2) return wrapIndex(startIndex + 1, total);
    return startIndex;
};

export type UseImageCarouselResult = {
    startIndex: number;
    activeIndex: number;
    jumpKey: number;
    alignKey: number;
    jumpTo: (index: number, total?: number) => void;
    goTo: (index: number) => void;
    setActiveIndex: Dispatch<SetStateAction<number>>;
};

export function useImageCarousel(length: number): UseImageCarouselResult {
    const [startIndex, setStartIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [jumpKey, setJumpKey] = useState(0);
    const [alignKey, setAlignKey] = useState(0);
    const lengthRef = useRef(length);
    lengthRef.current = length;

    useEffect(() => {
        if (length <= 0) return;
        if (startIndex < length && activeIndex < length) return;
        const next = wrapIndex(activeIndex, length);
        setStartIndex(next);
        setActiveIndex(next);
    }, [activeIndex, length, startIndex]);

    const goTo = useCallback((index: number) => {
        const total = lengthRef.current;
        if (total <= 0) return;
        const next = wrapIndex(index, total);
        setStartIndex(next);
        setActiveIndex(next);
        setAlignKey((key) => key + 1);
    }, []);

    const jumpTo = useCallback((index: number, total = lengthRef.current) => {
        if (total <= 0) return;
        const next = wrapIndex(index, total);
        setStartIndex(next);
        setActiveIndex(next);
        setJumpKey((key) => key + 1);
    }, []);

    return { startIndex, activeIndex, jumpKey, alignKey, jumpTo, goTo, setActiveIndex };
}

const getCarouselTrack = (root: HTMLElement) => {
    const track = Array.from(root.querySelectorAll<HTMLElement>("div")).find(
        (element) => element.classList.contains("flex") && element.style.gap,
    );
    return { track, scroller: track?.parentElement ?? null };
};

const readCarouselOffset = (root: HTMLElement) => {
    const { track, scroller } = getCarouselTrack(root);
    if (!track || !scroller) return null;

    const children = Array.from(track.children) as HTMLElement[];
    if (!children.length) return null;

    const offset = scroller.scrollLeft;
    let closest = 0;
    let minDistance = Number.POSITIVE_INFINITY;
    children.forEach((child, index) => {
        const distance = Math.abs(child.offsetLeft - offset);
        if (distance < minDistance) {
            minDistance = distance;
            closest = index;
        }
    });

    return closest;
};

function ColourPopupCarousel({
    slides,
    startIndex = 0,
    activeIndex = 0,
    jumpKey = 0,
    alignKey = 0,
    onSlideClick,
    onIndexChange,
    onNavigate,
    onStep,
    countOffset = 0,
    countTotal,
    frameClassName = "bg-[#1A1A1A]",
    priority = false,
    variant = "popup",
}: ImagePageCarouselProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const lastIndexRef = useRef(startIndex);
    const prevJumpKeyRef = useRef(jumpKey);
    const onIndexChangeRef = useRef(onIndexChange);
    const onNavigateRef = useRef(onNavigate);
    const onStepRef = useRef(onStep);
    const startIndexRef = useRef(startIndex);
    const activeIndexRef = useRef(activeIndex);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
    );
    onIndexChangeRef.current = onIndexChange;
    onNavigateRef.current = onNavigate;
    onStepRef.current = onStep;
    startIndexRef.current = startIndex;
    activeIndexRef.current = activeIndex;

    const isPopup = variant === "popup";
    const total = slides.length;
    const orderedSlides = isPopup && isMobile ? slides : rotate(slides, startIndex);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 767px)");
        const updateViewport = () => setIsMobile(mediaQuery.matches);
        updateViewport();
        mediaQuery.addEventListener("change", updateViewport);
        return () => mediaQuery.removeEventListener("change", updateViewport);
    }, []);

    useEffect(() => {
        if (!isMobile || !total) return;

        let cancelled = false;
        let attempts = 0;
        let frame = 0;
        const target = isPopup ? activeIndexRef.current : 0;

        const tryScroll = () => {
            if (cancelled || !rootRef.current) return;
            const { track, scroller } = getCarouselTrack(rootRef.current);
            const child = track?.children[target] as HTMLElement | undefined;
            if (!scroller || !child) {
                if (attempts < 20) {
                    attempts += 1;
                    frame = requestAnimationFrame(tryScroll);
                }
                return;
            }
            scroller.scrollTo({ left: child.offsetLeft, behavior: "auto" });
            lastIndexRef.current = activeIndexRef.current;
            prevJumpKeyRef.current = jumpKey;
        };

        frame = requestAnimationFrame(tryScroll);
        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
        };
    }, [alignKey, isMobile, isPopup, jumpKey, total]);

    useEffect(() => {
        lastIndexRef.current = isMobile ? activeIndex : startIndex;
        const root = rootRef.current;
        if (!root || !total) return;

        const onArrowClick = (event: Event) => {
            const button = (event.target as HTMLElement).closest("button");
            const label = button?.getAttribute("aria-label");
            if (label !== "Previous" && label !== "Next") return;

            event.preventDefault();
            event.stopPropagation();
            (event as MouseEvent).stopImmediatePropagation?.();

            const delta = label === "Next" ? 1 : -1;
            if (onStepRef.current?.(delta)) return;
            onNavigateRef.current?.(wrapIndex(activeIndexRef.current + delta, total));
        };

        let frame = 0;
        const emitSwipe = () => {
            if (!isMobile) return;

            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const offset = readCarouselOffset(root);
                if (offset == null) return;
                const next = isPopup && isMobile
                    ? wrapIndex(offset, total)
                    : wrapIndex(startIndexRef.current + offset, total);
                if (next === lastIndexRef.current) return;
                lastIndexRef.current = next;
                onIndexChangeRef.current?.(next);
            });
        };

        let touchStartX = 0;
        let touchStartIndex = 0;
        const onTouchStart = (event: TouchEvent) => {
            touchStartX = event.touches[0]?.clientX ?? 0;
            touchStartIndex = activeIndexRef.current;
        };
        const onTouchEnd = (event: TouchEvent) => {
            if (!isMobile || !isPopup) return;
            const dx = (event.changedTouches[0]?.clientX ?? 0) - touchStartX;
            if (Math.abs(dx) < 40) return;
            if (dx > 0 && touchStartIndex === 0) {
                onStepRef.current?.(-1);
                return;
            }
            if (dx < 0 && touchStartIndex === total - 1) {
                onStepRef.current?.(1);
            }
        };

        root.addEventListener("click", onArrowClick, true);
        root.addEventListener("scroll", emitSwipe, { capture: true, passive: true });
        root.addEventListener("touchstart", onTouchStart, { passive: true });
        root.addEventListener("touchend", onTouchEnd, { passive: true });

        return () => {
            cancelAnimationFrame(frame);
            root.removeEventListener("click", onArrowClick, true);
            root.removeEventListener("scroll", emitSwipe, true);
            root.removeEventListener("touchstart", onTouchStart);
            root.removeEventListener("touchend", onTouchEnd);
        };
    }, [activeIndex, isMobile, isPopup, jumpKey, startIndex, total]);

    if (!total) return null;

    const stepPopup = (delta: -1 | 1) => {
        if (onStepRef.current?.(delta)) return;
        onNavigateRef.current?.(wrapIndex(activeIndexRef.current + delta, total));
    };

    const popupArrowClass =
        "hidden md:flex h-10 w-8 items-center justify-center rounded-md bg-[#4a4d51]/85 md:h-12";

    return (
        <div ref={rootRef} className="relative">
            <Carousel
                key={jumpKey}
                arrowVariant="chevron"
                className={isPopup ? POPUP_IMAGE_CAROUSEL_CLASS : MAIN_CAROUSEL_CLASS}
                gap={8}
                itemsPerView={1}
                loop
                mobileItemsPerView={isPopup ? 1 : 1.15}
                showArrows={!isPopup}
                showPagination={false}
            >
                {orderedSlides.map((slide, index) => {
                    const originalIndex = isPopup && isMobile ? index : wrapIndex(startIndex + index, total);
                    return (
                        <div
                            key={slide.id}
                            aria-label={slide.label}
                            title={slide.title}
                            className={`relative overflow-hidden rounded-2xl ${
                                isPopup
                                    ? "h-[188px] w-full md:h-[450px] md:w-[901px]"
                                    : "h-[188px] border border-[#eee] md:aspect-[16/10] md:h-auto"
                            } ${frameClassName} ${onSlideClick ? "cursor-pointer" : ""}`}
                            onClick={() => onSlideClick?.(originalIndex)}
                        >
                            {slide.src ? (
                                <ImageWrapper
                                    src={slide.src}
                                    alt={slide.alt}
                                    title={slide.title}
                                    width={isPopup ? 901 : 960}
                                    height={isPopup ? 450 : 600}
                                    draggable={false}
                                    priority={priority && originalIndex === 0}
                                    className="pointer-events-none !h-full !w-full !max-h-full !max-w-full select-none object-cover"
                                />
                            ) : (
                                <div className="h-full w-full bg-black" aria-hidden />
                            )}
                            <span className={`${CHIP_CLASS} bottom-2 left-2 flex items-center gap-1.5 md:left-4`}>
                                {slide.swatch ? (
                                    <span
                                        className="h-2.5 w-2.5 shrink-0 rounded-full border border-white/40"
                                        style={{ background: slide.swatch }}
                                    />
                                ) : null}
                                {slide.label}
                            </span>
                            <span className={`${CHIP_CLASS} bottom-2 right-2 md:right-4`}>
                                {countOffset + originalIndex + 1} / {countTotal ?? total}
                            </span>
                        </div>
                    );
                })}
            </Carousel>
            {isPopup && total > 1 && (
                <>
                    <button
                        type="button"
                        aria-label="Previous slide"
                        onClick={() => stepPopup(-1)}
                        className={`${popupArrowClass} absolute left-3 top-1/2 z-10 -translate-y-1/2 cursor-pointer`}
                    >
                        <PopupChevron direction="prev" color="#ffffff" />
                    </button>
                    <button
                        type="button"
                        aria-label="Next slide"
                        onClick={() => stepPopup(1)}
                        className={`${popupArrowClass} absolute right-3 top-1/2 z-10 -translate-y-1/2 cursor-pointer`}
                    >
                        <PopupChevron direction="next" color="#ffffff" />
                    </button>
                </>
            )}
        </div>
    );
}

function ImageGalleryCarousel({
    slides,
    startIndex = 0,
    activeIndex = 0,
    jumpKey = 0,
    onSlideClick,
    onIndexChange,
    onNavigate,
    frameClassName = "bg-[#1A1A1A]",
    sizes = HERO_LCP_SIZES,
    priority = false,
    isMobile = false,
    variant = "hero",
}: ImagePageCarouselProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const lastIndexRef = useRef(startIndex);
    const onIndexChangeRef = useRef(onIndexChange);
    const onNavigateRef = useRef(onNavigate);
    const startIndexRef = useRef(startIndex);
    const activeIndexRef = useRef(activeIndex);
    onIndexChangeRef.current = onIndexChange;
    onNavigateRef.current = onNavigate;
    startIndexRef.current = startIndex;
    activeIndexRef.current = activeIndex;

    const isPopup = variant === "popup";
    const total = slides.length;
    const { rendered: renderedSlides, centerOffset, threeUp } = buildSlideWindow(
        slides,
        startIndex,
        total,
        isMobile,
        isPopup,
    );
    const threeUpRef = useRef(threeUp);
    const centerOffsetRef = useRef(centerOffset);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const touchNavLockRef = useRef(false);
    threeUpRef.current = threeUp;
    centerOffsetRef.current = centerOffset;

    useEffect(() => {
        if (!total) return;

        let cancelled = false;
        let attempts = 0;
        let frame = 0;

        const tryScroll = () => {
            if (cancelled || !rootRef.current) return;
            const { track, scroller } = getCarouselTrack(rootRef.current);
            const targetIndex = centerOffsetRef.current;
            const child = track?.children[targetIndex] as HTMLElement | undefined;
            if (!scroller || !child) {
                if (attempts < 20) {
                    attempts += 1;
                    frame = requestAnimationFrame(tryScroll);
                }
                return;
            }
            scroller.scrollTo({ left: child.offsetLeft, behavior: "auto" });
            lastIndexRef.current = activeIndexRef.current;
        };

        frame = requestAnimationFrame(tryScroll);
        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
        };
    }, [jumpKey, total, startIndex, centerOffset]);

    useEffect(() => {
        lastIndexRef.current = activeIndex;
        const root = rootRef.current;
        if (!root || !total) return;

        const onArrowClick = (event: Event) => {
            const button = (event.target as HTMLElement).closest("button");
            const label = button?.getAttribute("aria-label");
            if (label !== "Previous" && label !== "Next") return;

            event.preventDefault();
            event.stopPropagation();
            (event as MouseEvent).stopImmediatePropagation?.();

            const delta = label === "Next" ? 1 : -1;
            onNavigateRef.current?.(wrapIndex(activeIndexRef.current + delta, total));
        };

        let frame = 0;
        const emitSwipe = () => {
            if (!window.matchMedia("(max-width: 767px)").matches) return;

            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                if (touchNavLockRef.current) return;
                const offset = readCarouselOffset(root);
                if (offset == null) return;

                let next: number;
                if (threeUpRef.current) {
                    next = indexForWindowSlot(offset, startIndexRef.current, total, true);
                    if (offset === centerOffsetRef.current && next === startIndexRef.current) {
                        return;
                    }
                } else {
                    next = wrapIndex(startIndexRef.current + offset, total);
                }

                if (next === lastIndexRef.current) return;
                lastIndexRef.current = next;
                if (threeUpRef.current && offset !== centerOffsetRef.current) {
                    onNavigateRef.current?.(next);
                    return;
                }
                onIndexChangeRef.current?.(next);
            });
        };

        const onTouchStart = (event: TouchEvent) => {
            if (!isMobileViewport()) return;
            const touch = event.touches[0];
            if (!touch) return;
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
        };

        const onTouchEnd = (event: TouchEvent) => {
            if (!isMobileViewport()) return;
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (!start) return;

            const touch = event.changedTouches[0];
            if (!touch) return;

            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

            const next =
                dx > 0
                    ? wrapIndex(activeIndexRef.current - 1, total)
                    : wrapIndex(activeIndexRef.current + 1, total);
            if (next === activeIndexRef.current) return;

            touchNavLockRef.current = true;
            lastIndexRef.current = next;
            onNavigateRef.current?.(next);
            window.setTimeout(() => {
                touchNavLockRef.current = false;
            }, 320);
        };

        root.addEventListener("click", onArrowClick, true);
        root.addEventListener("scroll", emitSwipe, { capture: true, passive: true });
        root.addEventListener("touchstart", onTouchStart, { passive: true });
        root.addEventListener("touchend", onTouchEnd, { passive: true });

        return () => {
            cancelAnimationFrame(frame);
            root.removeEventListener("click", onArrowClick, true);
            root.removeEventListener("scroll", emitSwipe, true);
            root.removeEventListener("touchstart", onTouchStart);
            root.removeEventListener("touchend", onTouchEnd);
        };
    }, [activeIndex, jumpKey, startIndex, total]);

    if (!total) return null;

    return (
        <div ref={rootRef} className="relative w-full overflow-visible">
            <Carousel
                key={jumpKey}
                arrowVariant="chevron"
                className={MAIN_CAROUSEL_CLASS}
                gap={0}
                itemsPerView={1}
                loop
                mobileItemsPerView={1}
                showArrows
                showPagination={false}
            >
                {renderedSlides.map((slide, index) => {
                    const originalIndex = indexForWindowSlot(index, startIndex, total, threeUp);
                    const isVisibleSlide = index === centerOffset;
                    const isLcpSlide = Boolean(priority && !isPopup && isVisibleSlide && startIndex === 0);
                    return (
                        <div
                            key={threeUp ? `${slide.id}-slot-${index}` : slide.id}
                            aria-label={slide.label}
                            className={`relative overflow-hidden rounded-2xl ${frameClassName} ${onSlideClick ? "cursor-pointer" : ""}`}
                            onClick={() => onSlideClick?.(originalIndex)}
                            title={slide.alt}
                        >
                            {isLcpSlide ? (
                                <img
                                    src={getImagePageLcpUrl(slide.src, isMobile)}
                                    srcSet={getImagePageLcpSrcSet(slide.src)}
                                    sizes={sizes}
                                    width={HERO_LCP_ASPECT.width}
                                    height={HERO_LCP_ASPECT.height}
                                    alt={slide.alt}
                                    title={slide.alt}
                                    fetchPriority="high"
                                    loading="eager"
                                    decoding="async"
                                    draggable={false}
                                    className="pointer-events-none h-auto w-full select-none object-cover"
                                />
                            ) : isPopup ? (
                                <div className="relative aspect-[16/10]" title={slide.alt}>
                                    <ImageWrapper
                                        src={slide.src}
                                        alt={slide.alt}
                                        title={slide.alt}
                                        fill
                                        sourceWidth={682}
                                        sizes={sizes}
                                        draggable={false}
                                        className="pointer-events-none h-full w-full select-none object-cover"
                                    />
                                </div>
                            ) : (
                                <img
                                    src={getImagePageCdnUrl(slide.src, isMobile ? 640 : 828)}
                                    alt={slide.alt}
                                    width={HERO_LCP_ASPECT.width}
                                    height={HERO_LCP_ASPECT.height}
                                    loading={isVisibleSlide ? "eager" : "lazy"}
                                    decoding="async"
                                    fetchPriority="low"
                                    draggable={false}
                                    className="pointer-events-none h-auto w-full select-none object-cover"
                                />
                            )}
                            <span className={`${CHIP_CLASS} bottom-2 left-2 md:left-4`}>
                                {slide.label}
                            </span>
                            <span className={`${CHIP_CLASS} bottom-2 right-2 md:right-4`}>
                                {originalIndex + 1} / {total}
                            </span>
                        </div>
                    );
                })}
            </Carousel>
        </div>
    );
}

export default function ImagePageCarousel(props: ImagePageCarouselProps) {
    if (props.onStep) {
        return <ColourPopupCarousel {...props} />;
    }
    return <ImageGalleryCarousel {...props} />;
}

type ImagePageThumbCarouselProps = {
    slides: ImagePageCarouselSlide[];
    activeId?: string | number;
    onThumbClick?: (index: number) => void;
    frameClassName?: string;
    itemsPerView?: number;
    mobileItemsPerView?: number;
};

export function ImagePageThumbCarousel({
    slides,
    activeId,
    onThumbClick,
    frameClassName = "bg-[#1A1A1A]",
    itemsPerView = 7,
    mobileItemsPerView = 4.2,
}: ImagePageThumbCarouselProps) {
    const thumbsRef = useRef<HTMLDivElement>(null);
    const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
    const tapHandledRef = useRef(false);
    const [loadThumbs, setLoadThumbs] = useState(false);

    useEffect(() => {
        let timeoutId = window.setTimeout(() => setLoadThumbs(true), 1200);
        const idleId = window.requestIdleCallback?.(() => {
            window.clearTimeout(timeoutId);
            setLoadThumbs(true);
        });
        return () => {
            window.clearTimeout(timeoutId);
            if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
        };
    }, []);

    useEffect(() => {
        const root = thumbsRef.current;
        const thumb = root?.querySelector<HTMLElement>(`[data-thumb-id="${activeId}"]`);
        const scroller = root?.querySelector<HTMLElement>(":scope > div > div");
        const slide = thumb?.closest<HTMLElement>("[class*='shrink-0']") ?? thumb;
        if (!scroller || !slide) return;

        scroller.scrollTo({
            left: slide.offsetLeft - scroller.clientWidth / 2 + slide.clientWidth / 2,
            behavior: "smooth",
        });
    }, [activeId]);

    const activateThumb = (index: number) => {
        onThumbClick?.(index);
    };

    if (slides.length < 2) return null;

    return (
        <div ref={thumbsRef} className="relative z-20 h-[52px] w-full md:h-[70px]">
            <Carousel
                className={THUMB_CAROUSEL_CLASS}
                gap={8}
                itemsPerView={itemsPerView}
                mobileItemsPerView={mobileItemsPerView}
                showArrows={false}
                showPagination={false}
            >
                {slides.map((slide, index) => (
                    <button
                        key={slide.id}
                        type="button"
                        data-thumb-id={slide.id}
                        aria-label={slide.label || slide.alt}
                        aria-current={slide.id === activeId}
                        onPointerDown={(event) => {
                            pointerStartRef.current = { x: event.clientX, y: event.clientY };
                            tapHandledRef.current = false;
                        }}
                        onPointerUp={(event) => {
                            const start = pointerStartRef.current;
                            pointerStartRef.current = null;
                            if (!start) return;
                            if (
                                Math.abs(event.clientX - start.x) > TAP_THRESHOLD ||
                                Math.abs(event.clientY - start.y) > TAP_THRESHOLD
                            ) {
                                return;
                            }
                            tapHandledRef.current = true;
                            activateThumb(index);
                        }}
                        onPointerCancel={() => {
                            pointerStartRef.current = null;
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (tapHandledRef.current) {
                                tapHandledRef.current = false;
                                return;
                            }
                            activateThumb(index);
                        }}
                        className={`relative h-[52px] w-full touch-manipulation select-none overflow-hidden rounded-lg border md:h-[70px] ${
                            slide.id === activeId ? "border-2 border-[#008C8B]" : "border-[#eee]"
                        } ${frameClassName}`}
                    >
                        {loadThumbs ? (
                            <img
                                src={getImagePageCdnUrl(slide.src, 196)}
                                alt={slide.alt}
                                width={98}
                                height={70}
                                loading="lazy"
                                decoding="async"
                                fetchPriority="low"
                                draggable={false}
                                className="pointer-events-none h-full w-full select-none object-cover"
                            />
                        ) : null}
                    </button>
                ))}
            </Carousel>
        </div>
    );
}
